// Narration & Avatar guide — the AI voiceover + talking-avatar modal (add_sound_modal/),
// opened from both the "Sound" and "Add Avatar" nav buttons via showAddSoundModalNew,
// plus the canvas "Avatarları canlandır" control (avatar_animate/).
// Locale-keyed like every guide (content.<lang> → { feature, title, sections }).

const narrationAvatarGuide = {
  id: "narration-avatar",
  icon: "record_voice_over",
  keywords: [
    "sound",
    "ses",
    "narration",
    "seslendirme",
    "voice",
    "anlatım sesi",
    "voiceover",
    "text to speech",
    "metin okuma",
    "elevenlabs",
    "azure",
    "google",
    "narrate all scenes",
    "tüm sahneleri seslendir",
    "draft",
    "taslak",
    "save all",
    "tümünü kaydet",
    "clone voice",
    "ses klonla",
    "record voice",
    "sesimi kaydet",
    "upload audio",
    "dosya yükle",
    "avatar",
    "presenter",
    "sunucu",
    "talking head",
    "lip sync",
    "dudak senkronizasyonu",
    "transparent background",
    "şeffaf arka plan",
    "animate avatars",
    "avatarları canlandır",
    "activity log",
    "etkinlik günlüğü",
    "credits",
    "kredi",
    "narración",
    "voz",
    "sonido",
    "texto a voz",
    "narrar todas las escenas",
    "borrador",
    "guardar todo",
    "clonar voz",
    "grabar mi voz",
    "subir audio",
    "presentador",
    "sincronización labial",
    "fondo transparente",
    "animar avatares",
    "registro de actividad",
    "créditos",
  ],
  content: {
    en: {
      feature: "AI",
      title: "Narration & Avatar",
      sections: [
        {
          id: "overview",
          title: "Narration and avatar overview",
          videoUrl: null,
          text:
            "Two buttons among the AI tools in the top bar open the same modal on different tabs: **Sound** opens **AI Narration Voice**, and **Add Avatar** opens **Avatar Narration**. The modal also has **From Library**, **Record My Voice** and **Upload Your File** tabs if you'd rather use existing or your own audio, and the clock icon in its header opens the **Activity Log**.\n\n" +
            "- AI narration and avatars spend **credits** — the **Credits** button in the footer shows the estimate before you generate.\n" +
            "- The **Scenes** list on the left lets you switch scenes without closing the modal; a dot marks scenes with an unsaved narration draft.\n" +
            "- Subtitles are generated from your scenes' narration (⋮ menu → **Subtitles**), and the speech in a video can be turned into narration text from its **AI Wizard** tab — both have their own guides.",
        },
        {
          id: "narration",
          title: "Adding AI narration",
          videoUrl: null,
          text:
            "On the **AI Narration Voice** tab, type or edit the scene's narration in **Voice Text**, choose a narrator, then click **Generate Voice**.\n\n" +
            "- The narrator button (**Select Narrator**, or **Audio Narration** once a voice is chosen) opens the voice picker. Under **AI Voices**, filter by provider (ElevenLabs 2.5, ElevenLabs 3, Google, Azure), by language (Azure) and by gender, then pick a voice card. Under **Clone Voice** you narrate with a voice you've cloned.\n" +
            "- **Voice Tags** shows or hides the voice markup tags inside the text.\n" +
            "- Play the generated voice to check it; the gear button opens **Sound Settings** for its volume. After changing the text or voice, click **Regenerate Voice**.\n" +
            "- Click **Save** to add the narration to the scene. The **Narration Audio** list at the top reopens an existing narration of the scene, and **Create New Narration** starts another one.\n" +
            "- After you generate a new voice for a scene's main narration, a **Narrate all scenes** checkbox appears in the footer — tick it and confirm to re-narrate every scene with the same voice; it finishes in the background.",
        },
        {
          id: "avatar",
          title: "Creating a talking avatar",
          videoUrl: null,
          text:
            "On the **Avatar Narration** tab, work through the two steps, then click **Create Avatar** in the footer.\n\n" +
            "- **Avatar** — pick a ready presenter from the **Aidea Avatar Library**, or choose **Create your own**: **Generate with Prompt** (describe it freely), **Generate with Guide** (answer a few quick questions) or **Use your own media** (upload a photo or video, or use your camera), then **Create & Use Avatar**. Saved avatars can be reused in all your projects.\n" +
            "- **Voice & narration** — **Use scene audio** makes the avatar speak the scene's existing narration (not offered for every presenter). **Generate new voice / Edit scene audio** lets you edit the text (**Fetch Scene Text** brings in the scene's narration) and choose a provider and voice.\n" +
            "- **Transparent Background** removes the avatar's background. **Advanced Settings** holds the **Aspect Ratio** (**Square (1:1)** or **Landscape (16:9)**) and the lip-sync options.\n" +
            "- Rendering can take a few minutes — keep the window open. When it's done, **Add to Scene** places the video on the current scene, or **Regenerate** takes you back to the form to try again.",
        },
        {
          id: "scene-drafts",
          title: "Drafts and saving several scenes",
          videoUrl: null,
          text:
            "Voices are generated in the background, so you can move to another scene in the **Scenes** list while one is being made.\n\n" +
            "- A voice you haven't saved yet waits on its scene as a **Draft**. Drafts are kept only until you reload the page.\n" +
            "- On a ready draft, **Approve** applies it to that scene only and **Discard draft** throws it away; **Show original** shows the text as it was before your edits.\n" +
            "- If you edit the text after the voice was generated, generate the voice again before approving it.\n" +
            "- When several scenes have drafts, **Save all** in the footer saves every ready draft at once; it stays disabled until all voices have finished generating.",
        },
        {
          id: "clone-voice",
          title: "Cloning your voice",
          videoUrl: null,
          text:
            "On the **AI Narration Voice** tab, open the narrator button (**Select Narrator** or **Audio Narration**) and switch to **Clone Voice**.\n\n" +
            "- Under **Clone Your Voice**, either **Upload Voice File** with a sample of your voice or **Record with Microphone** while reading the text shown.\n" +
            "- Enter a **Voice Name**, choose the gender, then click **Clone Voice**.\n" +
            "- Your voices are listed under **Your Cloned Voices** — pick one and generate the narration as usual.",
        },
        {
          id: "own-audio",
          title: "Library, recording and upload",
          videoUrl: null,
          text:
            "- **From Library** — select one or more audio files you've already uploaded (Shift-click selects a range), then click **Add Sound**.\n" +
            "- **Record My Voice** — record with your microphone, listen back, optionally name the recording, then click **Save and Add**.\n" +
            "- **Upload Your File** — drop or pick an MP3, WAV or M4A file (up to 100 MB), then click **Save and Add**.",
        },
        {
          id: "activity-log",
          title: "Activity Log",
          videoUrl: null,
          text: "Click the clock icon in the modal header to see this training's past narration and avatar jobs, with their type, model, date and status, plus your **Current Balance** and spending. Double-click a row, or use its **+** button, to reuse a past narration or avatar video on the selected scene **without spending credits again**.",
        },
        {
          id: "animate-presenters",
          title: "Animating avatar presenters",
          videoUrl: null,
          text:
            "When your course contains avatar presenters that are still images (for example, ones placed when the course was generated), an **Animate avatars** button appears at the bottom right of the canvas, above the zoom control.\n\n" +
            "- **Animate the avatar in this scene** animates the presenter on the current scene; **Animate avatars in all scenes** animates every presenter in the course. Only presenters that already have narration audio and no clip yet are animated.\n" +
            "- Each finished talking clip replaces its still image automatically.\n" +
            "- **History** lists earlier takes for the current scene; **Apply** switches the presenter back to one of them.",
        },
      ],
    },
    tr: {
      feature: "Yapay Zeka",
      title: "Seslendirme ve Avatar",
      sections: [
        {
          id: "overview",
          title: "Seslendirme ve avatara genel bakış",
          videoUrl: null,
          text:
            "Üst çubuktaki yapay zeka araçlarından iki düğme aynı modalı farklı sekmelerde açar: **Anlatım Sesi** düğmesi **AI Anlatım Sesi** sekmesini, **Avatar Ekle** düğmesi ise **Avatar Anlatımı** sekmesini açar. Hazır ya da kendi sesinizi kullanmak isterseniz modalda **Kütüphaneden Seç**, **Sesimi Kaydet** ve **Dosya Yükle** sekmeleri de bulunur; başlıktaki saat simgesi **Etkinlik Günlüğü**'nü açar.\n\n" +
            "- Yapay zeka ile seslendirme ve avatar üretimi **kredi** harcar — üretmeden önce alt kısımdaki **Kredi** düğmesiyle tahmini maliyeti görün.\n" +
            "- Soldaki **Sahneler** listesi, modalı kapatmadan sahneler arasında geçiş yapmanızı sağlar; kaydedilmemiş anlatım taslağı olan sahneler bir noktayla işaretlenir.\n" +
            "- Altyazılar sahnelerinizin anlatımından üretilir (⋮ menüsü → **Altyazılar**); bir videodaki konuşma ise videonun **AI Wizard** sekmesinden anlatım metnine dönüştürülebilir — ikisinin de kendi rehberi vardır.",
        },
        {
          id: "narration",
          title: "AI seslendirme ekleme",
          videoUrl: null,
          text:
            "**AI Anlatım Sesi** sekmesinde sahnenin anlatım metnini **Ses Metni** alanına yazın veya düzenleyin, bir anlatıcı seçin, ardından **Ses Oluştur**'a tıklayın.\n\n" +
            "- Anlatıcı düğmesi (**Anlatıcı Seç**; bir ses seçiliyse **Anlatım Sesi**) ses seçiciyi açar. **AI Sesler** altında sağlayıcıya (ElevenLabs 2.5, ElevenLabs 3, Google, Azure), dile (Azure) ve cinsiyete göre filtreleyip bir ses kartı seçin. **Ses Klonla** altında klonladığınız bir sesle seslendirme yaparsınız.\n" +
            "- **Ses Etiketleri**, metnin içindeki ses biçimlendirme etiketlerini gösterir veya gizler.\n" +
            "- Üretilen sesi oynatarak kontrol edin; dişli düğmesi ses seviyesi için **Ses Ayarları**'nı açar. Metni veya sesi değiştirdikten sonra **Yeniden Oluştur**'a tıklayın.\n" +
            "- Anlatımı sahneye eklemek için **Kaydet**'e tıklayın. Üstteki **Anlatım Sesleri** listesi sahnenin mevcut bir anlatımını yeniden açar; **Yeni Anlatım Sesi Oluştur** ise yeni bir anlatım başlatır.\n" +
            "- Sahnenin ana anlatımı için yeni bir ses ürettiğinizde alt kısımda **Tüm sahneleri seslendir** kutusu görünür — işaretleyip onaylarsanız tüm sahneler aynı sesle yeniden seslendirilir; işlem arka planda tamamlanır.",
        },
        {
          id: "avatar",
          title: "Konuşan avatar oluşturma",
          videoUrl: null,
          text:
            "**Avatar Anlatımı** sekmesinde iki adımı tamamlayın, ardından alt kısımdaki **Avatar Oluştur**'a tıklayın.\n\n" +
            "- **Avatar** — **Aidea Avatar Kütüphanesi**'nden hazır bir sunucu seçin ya da **Kendin oluştur**'u seçin: **Prompt ile üret** (serbestçe tarif edin), **Rehber ile üret** (birkaç kısa soruyu yanıtlayın) veya **Kendi medyanı kullan** (fotoğraf ya da video yükleyin veya kameranızı kullanın); ardından **Avatar'ı Oluştur ve Kullan**'a tıklayın. Kaydedilen avatarlar tüm projelerinizde kullanılabilir.\n" +
            "- **Ses ve anlatım** — **Sahne sesini kullan**, avatarın sahnenin mevcut anlatımını seslendirmesini sağlar (her sunucuda sunulmaz). **Yeni ses üret / Sahne sesi düzenle** ile metni düzenleyebilir (**Sahne Metnini Getir** sahnenin anlatımını getirir) ve bir sağlayıcı ile ses seçebilirsiniz.\n" +
            "- **Şeffaf Arka Plan** avatarın arka planını kaldırır. **Gelişmiş Ayarlar** altında **En/Boy Oranı** (**Kare (1:1)** veya **Yatay (16:9)**) ve dudak senkronizasyonu seçenekleri bulunur.\n" +
            "- Oluşturma birkaç dakika sürebilir — pencereyi açık tutun. Tamamlandığında videoyu mevcut sahneye yerleştirmek için **Sahneye Ekle**'yi, tekrar denemek üzere forma dönmek için **Yeniden Oluştur**'u kullanın.",
        },
        {
          id: "scene-drafts",
          title: "Taslaklar ve birden fazla sahneyi kaydetme",
          videoUrl: null,
          text:
            "Sesler arka planda üretilir; bu sırada **Sahneler** listesinden başka bir sahneye geçebilirsiniz.\n\n" +
            "- Henüz kaydetmediğiniz bir ses, sahnesinde **Taslak** olarak bekler. Taslaklar yalnızca sayfayı yenileyene kadar saklanır.\n" +
            "- Hazır bir taslakta **Onayla** onu yalnızca o sahneye uygular, **Taslağı at** ise siler; **Orijinali göster**, metnin düzenlemeden önceki hâlini gösterir.\n" +
            "- Ses üretildikten sonra metni değiştirirseniz, onaylamadan önce sesi yeniden oluşturun.\n" +
            "- Birden fazla sahnede taslak varsa alt kısımdaki **Tümünü kaydet** hazır taslakların hepsini tek seferde kaydeder; tüm seslerin üretimi bitene kadar devre dışı kalır.",
        },
        {
          id: "clone-voice",
          title: "Sesinizi klonlama",
          videoUrl: null,
          text:
            "**AI Anlatım Sesi** sekmesinde anlatıcı düğmesini (**Anlatıcı Seç** veya **Anlatım Sesi**) açın ve **Ses Klonla**'ya geçin.\n\n" +
            "- **Sesinizi Klonlayın** bölümünde ya **Ses Dosyası Yükle** ile sesinizden bir örnek yükleyin ya da **Mikrofonla Kaydet** ile gösterilen metni okuyarak kayıt yapın.\n" +
            "- Bir **Ses Adı** girin, cinsiyeti seçin, ardından **Sesi Klonla**'ya tıklayın.\n" +
            "- Sesleriniz **Klonlanmış Sesleriniz** altında listelenir — birini seçip anlatımı her zamanki gibi üretin.",
        },
        {
          id: "own-audio",
          title: "Kütüphane, kayıt ve yükleme",
          videoUrl: null,
          text:
            "- **Kütüphaneden Seç** — daha önce yüklediğiniz ses dosyalarından bir veya birkaçını seçin (Shift ile tıklayarak aralık seçebilirsiniz), ardından **Ses Ekle**'ye tıklayın.\n" +
            "- **Sesimi Kaydet** — mikrofonunuzla kayıt yapın, dinleyin, isterseniz kayda bir ad verin, ardından **Kaydet ve Ekle**'ye tıklayın.\n" +
            "- **Dosya Yükle** — bir MP3, WAV veya M4A dosyasını (en fazla 100 MB) bırakın ya da seçin, ardından **Kaydet ve Ekle**'ye tıklayın.",
        },
        {
          id: "activity-log",
          title: "Etkinlik Günlüğü",
          videoUrl: null,
          text: "Bu eğitimdeki geçmiş seslendirme ve avatar işlerini türü, modeli, tarihi ve durumuyla, **Mevcut Bakiye** ve harcamanızla birlikte görmek için modal başlığındaki saat simgesine tıklayın. Geçmiş bir anlatımı veya avatar videosunu **yeniden kredi harcamadan** seçili sahnede kullanmak için satıra çift tıklayın veya satırdaki **+** düğmesini kullanın.",
        },
        {
          id: "animate-presenters",
          title: "Avatar sunucuları canlandırma",
          videoUrl: null,
          text:
            "Kursunuzda hareketsiz görsel olarak duran avatar sunucular varsa (örneğin kurs üretilirken yerleştirilenler), tuvalin sağ altında, yakınlaştırma kontrolünün üstünde **Avatarları canlandır** düğmesi görünür.\n\n" +
            "- **Bu sahnedeki avatarı oynat** mevcut sahnedeki sunucuyu canlandırır; **Tüm sahnelerdeki avatarları oynat** ise kurstaki tüm sunucuları canlandırır. Yalnızca anlatım sesi olan ve henüz klibi bulunmayan sunucular canlandırılır.\n" +
            "- Tamamlanan her konuşma klibi, hareketsiz görselin yerini otomatik olarak alır.\n" +
            "- **Geçmiş**, mevcut sahnenin önceki çekimlerini listeler; **Uygula** ile sunucuyu bunlardan birine geri döndürebilirsiniz.",
        },
      ],
    },
    es: {
      feature: "IA",
      title: "Narración y avatar",
      sections: [
        {
          id: "overview",
          title: "Visión general de la narración y el avatar",
          videoUrl: null,
          text:
            "Dos botones de las herramientas de IA de la barra superior abren la misma ventana en pestañas distintas: **Sonido** abre **Voz de Narración AI** y **Agregar Avatar** abre **Narración de Avatar**. La ventana también tiene las pestañas **Desde la biblioteca**, **Grabar mi voz** y **Subir mi archivo** por si prefieres usar audio existente o propio, y el icono del reloj de su encabezado abre el **Registro de Actividad**.\n\n" +
            "- La narración y los avatares con IA consumen **créditos**; el botón **Créditos** del pie muestra la estimación antes de generar.\n" +
            "- La lista **Escenas** de la izquierda te permite cambiar de escena sin cerrar la ventana; un punto marca las escenas con un borrador de narración sin guardar.\n" +
            "- Los subtítulos se generan a partir de la narración de tus escenas (menú ⋮ → **Subtítulos**), y el habla de un vídeo puede convertirse en texto de narración desde su pestaña **AI Wizard**; ambos tienen su propia guía.",
        },
        {
          id: "narration",
          title: "Añadir narración con IA",
          videoUrl: null,
          text:
            "En la pestaña **Voz de Narración AI**, escribe o edita la narración de la escena en **Texto de Voz**, elige un narrador y haz clic en **Generar Voz**.\n\n" +
            "- El botón del narrador (**Seleccionar Narrador**, o **Narración de audio** una vez elegida una voz) abre el selector de voces. En **Voces AI**, filtra por proveedor (ElevenLabs 2.5, ElevenLabs 3, Google, Azure), por idioma (Azure) y por género, y elige una tarjeta de voz. En **Clonar Voz** narras con una voz que hayas clonado.\n" +
            "- **Etiquetas de Voz** muestra u oculta las etiquetas de marcado de voz dentro del texto.\n" +
            "- Reproduce la voz generada para comprobarla; el botón del engranaje abre **Configuración de Sonido** para ajustar su volumen. Si cambias el texto o la voz, haz clic en **Regenerar Voz**.\n" +
            "- Haz clic en **Guardar** para añadir la narración a la escena. La lista **Sonidos de Narración** de la parte superior vuelve a abrir una narración existente de la escena, y **Crear nueva narración** empieza otra.\n" +
            "- Después de generar una voz nueva para la narración principal de una escena, aparece la casilla **Narrar todas las escenas** en el pie; márcala y confirma para volver a narrar todas las escenas con la misma voz. El proceso termina en segundo plano.",
        },
        {
          id: "avatar",
          title: "Crear un avatar parlante",
          videoUrl: null,
          text:
            "En la pestaña **Narración de Avatar**, completa los dos pasos y haz clic en **Crear Avatar** en el pie.\n\n" +
            "- **Avatar**: elige un presentador ya preparado de la **Biblioteca de Avatares Aidea** o elige **Crea el tuyo**: **Generar con Prompt** (descríbelo libremente), **Generar con Guía** (responde unas preguntas rápidas) o **Usa tu propio contenido** (sube una foto o un vídeo, o usa tu cámara) y, después, **Crear y usar avatar**. Los avatares guardados se pueden reutilizar en todos tus proyectos.\n" +
            "- **Voz y narración**: **Usar audio de la escena** hace que el avatar diga la narración existente de la escena (no está disponible para todos los presentadores). **Generar nueva voz / Editar audio de escena** te permite editar el texto (**Obtener texto de la escena** trae la narración de la escena) y elegir un proveedor y una voz.\n" +
            "- **Fondo Transparente** quita el fondo del avatar. **Configuraciones Avanzadas** contiene la **Relación de Aspecto** (**Cuadrado (1:1)** u **Horizontal (16:9)**) y las opciones de sincronización labial.\n" +
            "- La generación puede tardar unos minutos; mantén la ventana abierta. Cuando termine, **Agregar a Escena** coloca el vídeo en la escena actual, o **Regenerar** te devuelve al formulario para intentarlo de nuevo.",
        },
        {
          id: "scene-drafts",
          title: "Borradores y guardar varias escenas",
          videoUrl: null,
          text:
            "Las voces se generan en segundo plano, así que puedes pasar a otra escena de la lista **Escenas** mientras se crea una.\n\n" +
            "- Una voz que aún no has guardado queda en su escena como **Borrador**. Los borradores solo se conservan hasta que recargas la página.\n" +
            "- En un borrador listo, **Aprobar** lo aplica solo a esa escena y **Descartar borrador** lo elimina; **Mostrar original** muestra el texto como estaba antes de tus cambios.\n" +
            "- Si editas el texto después de generar la voz, vuelve a generar la voz antes de aprobarla.\n" +
            "- Cuando varias escenas tienen borradores, **Guardar todo** en el pie guarda a la vez todos los borradores listos; permanece desactivado hasta que todas las voces han terminado de generarse.",
        },
        {
          id: "clone-voice",
          title: "Clonar tu voz",
          videoUrl: null,
          text:
            "En la pestaña **Voz de Narración AI**, abre el botón del narrador (**Seleccionar Narrador** o **Narración de audio**) y cambia a **Clonar Voz**.\n\n" +
            "- En **Clonar Tu Voz**, usa **Sube Tu Voz** con una muestra de tu voz o **Grabar con Micrófono** mientras lees el texto que se muestra.\n" +
            "- Introduce un **Nombre de Voz**, elige el género y haz clic en **Clonar Voz**.\n" +
            "- Tus voces aparecen en **Tus Voces Clonadas**: elige una y genera la narración como de costumbre.",
        },
        {
          id: "own-audio",
          title: "Biblioteca, grabación y subida",
          videoUrl: null,
          text:
            "- **Desde la biblioteca**: selecciona uno o varios archivos de audio que ya hayas subido (Mayús + clic selecciona un rango) y haz clic en **Agregar Sonido**.\n" +
            "- **Grabar mi voz**: graba con tu micrófono, escucha la grabación, ponle un nombre si quieres y haz clic en **Guardar y Agregar**.\n" +
            "- **Subir mi archivo**: suelta o elige un archivo MP3, WAV o M4A (de hasta 100 MB) y haz clic en **Guardar y Agregar**.",
        },
        {
          id: "activity-log",
          title: "Registro de Actividad",
          videoUrl: null,
          text: "Haz clic en el icono del reloj del encabezado de la ventana para ver los trabajos anteriores de narración y avatar de esta formación, con su tipo, modelo, fecha y estado, además de tu **Saldo Actual** y tu gasto. Haz doble clic en una fila, o usa su botón **+**, para reutilizar una narración o un vídeo de avatar anterior en la escena seleccionada **sin volver a gastar créditos**.",
        },
        {
          id: "animate-presenters",
          title: "Animar presentadores avatar",
          videoUrl: null,
          text:
            "Cuando tu curso contiene presentadores avatar que todavía son imágenes fijas (por ejemplo, los que se colocaron al generar el curso), aparece un botón **Animar avatares** en la esquina inferior derecha del lienzo, encima del control de zoom.\n\n" +
            "- **Animar el avatar de esta escena** anima al presentador de la escena actual; **Animar los avatares de todas las escenas** anima a todos los presentadores del curso. Solo se animan los presentadores que ya tienen audio de narración y aún no tienen clip.\n" +
            "- Cada clip parlante terminado sustituye automáticamente a su imagen fija.\n" +
            "- **Historial** muestra las tomas anteriores de la escena actual; **Aplicar** devuelve el presentador a una de ellas.",
        },
      ],
    },
  },
};

export default narrationAvatarGuide;
